import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { RealTimePage } from '@/features/realtime/components/RealTimePage'
import { resolveReturnToPath } from '@/lib/return-navigation'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { cn } from '@/lib/utils'

const SCHEDULING_TABS = [
  { value: '/job/realtime', label: 'Real Time' },
  { value: '/jobs/scheduling', label: 'Schedule' },
  { value: '/timeline', label: 'Timeline' },
  { value: '/workdays', label: 'Work Days' },
] as const

export default function RealtimeOperationsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  // Focused mode: opened from a job row's map icon (?job_id=&date=); absent = normal board.
  const focusParams = new URLSearchParams(location.search)
  const focusJobId = focusParams.get('job_id')?.trim() || null
  const focusDate = focusJobId ? focusParams.get('date') : null
  const focusReturnTo = focusJobId ? resolveReturnToPath(location.search, '') || null : null

  const activeSchedulingTab = useMemo(() => {
    const pathname = location.pathname
    if (pathname.startsWith('/timeline')) return '/timeline'
    if (pathname.startsWith('/workdays')) return '/workdays'
    if (pathname.startsWith('/jobs/scheduling') || pathname.startsWith('/job/scheduling') || pathname.startsWith('/job/schedule-workers')) {
      return '/jobs/scheduling'
    }
    return '/job/realtime'
  }, [location.pathname])

  const handleSchedulingTabNavigate = (nextPath: string) => {
    if (!nextPath || nextPath === activeSchedulingTab) {
      return
    }

    navigate(withServiceInstanceId(nextPath, serviceInstanceId))
  }

  const handleShowAllJobs = () => {
    const params = new URLSearchParams(location.search)
    params.delete('job_id')
    params.delete('date')
    const search = params.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true })
  }

  return (
    <div className="min-w-0 space-y-4 bg-white p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#dfeae3] bg-[#f7fbf8] p-2">
        {SCHEDULING_TABS.map((tab) => {
          const isActive = activeSchedulingTab === tab.value
          return (
            <Button
              key={tab.value}
              type="button"
              variant="ghost"
              onClick={() => handleSchedulingTabNavigate(tab.value)}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold',
                isActive
                  ? 'bg-[#169b62] text-white hover:bg-[#118753] hover:text-white'
                  : 'text-[#355445] hover:bg-[#e8f4ee]'
              )}
            >
              {tab.label}
            </Button>
          )
        })}
      </div>

      <RealTimePage
        // job_id is part of the key so "Show all jobs" remounts into the normal board.
        key={focusJobId ? `${serviceInstanceId ?? 'default'}|${focusJobId}` : serviceInstanceId ?? 'default'}
        serviceInstanceId={serviceInstanceId}
        focusJobId={focusJobId}
        focusDate={focusDate}
        onShowAllJobs={handleShowAllJobs}
        onBackToJobs={focusReturnTo ? () => navigate(focusReturnTo) : undefined}
      />
    </div>
  )
}
